import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Rocket, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { useGetApiRuntimeTarget } from "@/lib/api/generated/runtime-targets/runtime-targets";
import { useGetApiLiveSnapshot } from "@/lib/api/generated/state/state";
import {
  usePostApiSnapshotApply,
  usePostApiSnapshotCompile,
} from "@/lib/api/generated/workflows/workflows";
import { formatTimestamp, toastMutationError } from "@/lib/utils/formatting";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import type { TenantLiveSnapshotRecord } from "@/lib/types/live-snapshot";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { queryKeys } from "@/lib/query/keys";

export function SnapshotsPage() {
  const { tenantId } = useTenantContext();
  const queryClient = useQueryClient();
  const { data: liveSnapshotData } = useGetApiLiveSnapshot({
    query: {
      enabled: Boolean(tenantId),
      queryKey: queryKeys.liveSnapshot(tenantId ?? "none"),
      refetchInterval: 5000,
    },
  });
  const liveSnapshot = liveSnapshotData as unknown as TenantLiveSnapshotRecord | null | undefined;
  const { data: runtimeTarget } = useGetApiRuntimeTarget({
    query: {
      enabled: Boolean(tenantId),
      queryKey: queryKeys.tenantRuntimeTarget(tenantId ?? "none"),
    },
  });
  const compileMutation = usePostApiSnapshotCompile();
  const applyMutation = usePostApiSnapshotApply();
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  useDocumentTitle("Snapshots");

  const handleCompile = async () => {
    try {
      await compileMutation.mutateAsync({
        data: {},
      });
      toast.success("Snapshot compiled");
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to compile snapshot"));
    }
  };

  const snapshot = liveSnapshot?.snapshot;
  const validation = liveSnapshot?.validation;
  const lastApplyResponse = liveSnapshot?.lastApplyResponse;
  const lastApplyStatus = getLastApplyStatus(lastApplyResponse) ?? "pending";
  const lastApplyErrorMessage = getLastApplyErrorMessage(lastApplyResponse);

  const handleApply = async () => {
    setShowApplyConfirm(false);
    if (!runtimeTarget) {
      toast.error("No runtime target configured. Configure one in the Runtime tab first.");
      return;
    }
    try {
      await applyMutation.mutateAsync({
        data: {
          workspaceRootDir: runtimeTarget.workspaceRootDir,
          generatedDir: runtimeTarget.generatedDir,
          snapshotPath: runtimeTarget.snapshotPath,
        },
      });
      if (tenantId) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.liveSnapshot(tenantId) }),
          queryClient.invalidateQueries({ queryKey: queryKeys.tenantRuntimeTarget(tenantId) }),
          queryClient.invalidateQueries({ queryKey: queryKeys.state }),
        ]);
      }
      toast.success("Snapshot applied to runtime");
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to apply snapshot"));
    }
  };

  if (!snapshot || !validation) {
    return (
      <div className="flex flex-col gap-4">
        <EmptyState
          title="No live snapshot"
          description="Compile a snapshot to generate the runtime configuration."
          action={{ label: "Compile Snapshot", onClick: handleCompile }}
        />
      </div>
    );
  }

  const snapshotHashPreview = snapshot.snapshotHash
    ? `${snapshot.snapshotHash.slice(0, 16)}...`
    : "unknown";

  return (
    <div className="flex flex-col gap-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>Snapshots</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCompile}
            disabled={compileMutation.isPending}
          >
            <RefreshCw size={14} className={compileMutation.isPending ? "animate-spin" : ""} />
            {compileMutation.isPending ? "Compiling..." : "Compile"}
          </Button>
          <Button
            size="sm"
            onClick={() => setShowApplyConfirm(true)}
            disabled={applyMutation.isPending || !runtimeTarget}
          >
            <Rocket size={14} />
            {applyMutation.isPending ? "Applying..." : "Apply to Runtime"}
          </Button>
        </div>
      </div>

      {!runtimeTarget && (
        <div
          className="rounded-[var(--radius-sm)] border px-3 py-2 text-xs"
          style={{ borderColor: "var(--warn)", color: "var(--warn)", background: "var(--warn-subtle)" }}
        >
          No runtime target configured. Apply will be unavailable until a runtime target is set in the Runtime tab.
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Current Snapshot</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span style={{ color: "var(--muted-foreground)" }}>Generation</span>
              <p className="font-mono text-base font-bold" style={{ color: "var(--text-strong)" }}>
                {snapshot.generation ?? "—"}
              </p>
            </div>
            <div>
              <span style={{ color: "var(--muted-foreground)" }}>Hash</span>
              <p className="font-mono" style={{ color: "var(--text-strong)" }}>
                {snapshot.snapshotHash ?? "—"}
              </p>
            </div>
            <div>
              <span style={{ color: "var(--muted-foreground)" }}>Generated At</span>
              <p style={{ color: "var(--text-strong)" }}>{formatTimestamp(snapshot.generatedAt)}</p>
            </div>
            <div>
              <span style={{ color: "var(--muted-foreground)" }}>Apply Status</span>
              <div className="mt-0.5">
                <StatusBadge status={lastApplyStatus} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {lastApplyErrorMessage ? (
        <Card>
          <CardHeader>
            <CardTitle>Last Apply Error</CardTitle>
          </CardHeader>
          <CardContent>
            <pre
              className="text-xs font-mono p-3 rounded-[var(--radius-sm)] overflow-x-auto"
              style={{ background: "var(--danger-subtle)", color: "var(--danger)" }}
            >
              {lastApplyErrorMessage}
            </pre>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Validation</CardTitle>
        </CardHeader>
        <CardContent>
          <pre
            className="text-xs font-mono p-3 rounded-[var(--radius-sm)] overflow-x-auto"
            style={{ background: "var(--bg-elevated)", color: "var(--text)" }}
          >
            {JSON.stringify(validation, null, 2)}
          </pre>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={showApplyConfirm}
        onConfirm={handleApply}
        onCancel={() => setShowApplyConfirm(false)}
        title="Apply Snapshot to Runtime"
        description={`This will push generation ${snapshot.generation ?? "—"} (${snapshotHashPreview}) to the runtime at ${runtimeTarget?.baseUrl ?? "unknown"}. The runtime will restart with the new configuration.`}
        confirmLabel="Apply"
      />
    </div>
  );
}

function getLastApplyStatus(lastApplyResponse: unknown): string | undefined {
  if (!lastApplyResponse || typeof lastApplyResponse !== "object") {
    return undefined;
  }
  if (!("status" in lastApplyResponse)) {
    return undefined;
  }
  const status = (lastApplyResponse as { status?: unknown }).status;
  return typeof status === "string" ? status : undefined;
}

function getLastApplyErrorMessage(lastApplyResponse: unknown): string | undefined {
  if (!lastApplyResponse || typeof lastApplyResponse !== "object") {
    return undefined;
  }
  if (!("errorMessage" in lastApplyResponse)) {
    return undefined;
  }
  const errorMessage = (lastApplyResponse as { errorMessage?: unknown }).errorMessage;
  return typeof errorMessage === "string" && errorMessage.trim() ? errorMessage : undefined;
}
